import { VisibilityState } from '@tanstack/react-table'

import { AttributeKey, AttributeValue, DatableDataType, DatableLog, DatableTrace, TelemetryEvent } from '@ui/interfaces'
import { useNavigationStateStore } from '@ui/stores'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface NestedToggleType {
  [key: string]: VisibilityState
}

interface TelemetryStoreState {
  dataSource: DatableDataType
  whereClause: string
  rowCount: number
  selectedKey: AttributeKey
  selectedValue: AttributeValue
  selectedEvent: DatableLog | DatableTrace | undefined
  selectedEventOutput: DatableLog | DatableTrace | undefined
  logs: TelemetryEvent<DatableLog>[]
  customSampleLog: DatableLog | undefined
  spans: TelemetryEvent<DatableTrace>[]
  customSampleSpan: DatableTrace | undefined
  uniqueKeysLogs: AttributeKey[]
  uniqueKeysSpans: AttributeKey[]
  pinnedSampleEvents: boolean
  sampleEvents: {
    input: DatableLog | DatableTrace
    output: DatableLog | DatableTrace
  }[]
}

interface TelemetryStoreActions {
  resetStore: () => void
  setWhereClause: (by: string) => void
  setSelectedKey: (by: AttributeKey) => void
  setSelectedValue: (by: AttributeValue) => void
  setDataSource: (selectedDataSource: 'logs' | 'spans' | 'metrics') => void
  setRowCount: (rowCount: number) => void
  setSelectedEvent: (event: DatableLog | DatableTrace) => void
  setSelectedEventOutput: (event: DatableLog | DatableTrace) => void
  setLogs: (logs: TelemetryEvent<DatableLog>[]) => void
  setCustomSampleLog: (logs: DatableLog | undefined) => void
  setSpans: (spans: TelemetryEvent<DatableTrace>[]) => void
  setCustomSampleSpan: (spans: DatableTrace | undefined) => void
  setUniqueKeysLogs: (uniqueKeys: AttributeKey[]) => void
  setUniqueKeysSpans: (uniqueKeys: AttributeKey[]) => void
  setSampleEvents: (events: { input: DatableLog | DatableTrace; output: DatableLog | DatableTrace }[]) => void
  setPinnedSampleEvents: (pinned: boolean) => void
}

const initialState: TelemetryStoreState = {
  dataSource: 'logs',
  whereClause: '',
  rowCount: 0,
  selectedKey: {
    key: '',
    count: 0,
    size: 0,
  },
  selectedValue: {
    key: '',
    value: '',
    count: 0,
    size: 0,
    ratio: 0,
    queryProps: {
      operator: '',
      value: '',
    },
  },
  selectedEvent: undefined,
  selectedEventOutput: undefined,
  logs: [],
  customSampleLog: undefined,
  spans: [],
  customSampleSpan: undefined,
  uniqueKeysLogs: [],
  uniqueKeysSpans: [],
  pinnedSampleEvents: false,
  sampleEvents: [],
}

export const useTelemetryStore = create(
  persist<TelemetryStoreState & TelemetryStoreActions>(
    (set) => ({
      // State
      ...initialState,

      // Actions
      resetStore: () => {
        set(() => ({
          ...initialState,
        }))
      },
      setWhereClause: (by: string) => {
        useNavigationStateStore.getState().setQueryParams({ query: by })
        set(() => {
          return { whereClause: by }
        })
      },
      setSelectedKey: (by: AttributeKey) => {
        useNavigationStateStore.getState().setQueryParams({ groupBy: by.key })
        set(() => {
          return { selectedKey: by }
        })
      },
      setSelectedValue: (by: AttributeValue) => {
        set(() => {
          return { selectedValue: by }
        })
      },
      setDataSource: (selectedDataSource: DatableDataType) => {
        useNavigationStateStore.getState().setQueryParams({ datatype: selectedDataSource as DatableDataType })
        set(() => {
          return { dataSource: selectedDataSource }
        })
      },
      setRowCount: (rowCount: number) => {
        set(() => {
          return { rowCount: rowCount }
        })
      },
      setSelectedEvent: (event: DatableLog | DatableTrace) => {
        set(() => {
          return { selectedEvent: event }
        })
      },
      setSelectedEventOutput: (event: DatableLog | DatableTrace) => {
        set(() => {
          return { selectedEventOutput: event }
        })
      },
      setLogs: (logs: TelemetryEvent<DatableLog>[]) => {
        set(() => {
          return { logs: logs }
        })
      },
      setCustomSampleLog: (logs: DatableLog | undefined) => {
        set(() => {
          return { customSampleLog: logs }
        })
      },
      setSpans: (spans: TelemetryEvent<DatableTrace>[]) => {
        set(() => {
          return { spans: spans }
        })
      },
      setCustomSampleSpan: (spans: DatableTrace | undefined) => {
        set(() => {
          return { customSampleSpan: spans }
        })
      },
      setUniqueKeysLogs: (uniqueKeys: AttributeKey[]) => {
        set(() => {
          return { uniqueKeysLogs: uniqueKeys }
        })
      },
      setUniqueKeysSpans: (uniqueKeys: AttributeKey[]) => {
        set(() => {
          return { uniqueKeysSpans: uniqueKeys }
        })
      },
      setSampleEvents: (events: { input: DatableLog | DatableTrace; output: DatableLog | DatableTrace }[]) => {
        set(() => {
          return { sampleEvents: events }
        })
      },
      setPinnedSampleEvents: (pinned: boolean) => {
        set(() => {
          return { pinnedSampleEvents: pinned }
        })
      },
    }),
    { name: 'telemetry-table-store' }
  )
)
